import React from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Skeleton,
  Chip,
  useTheme
} from '@mui/material'
import {
  TrendingUp,
  TrendingDown,
  TrendingFlat
} from '@mui/icons-material'

const KPICard = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  color = 'primary', 
  loading = false,
  trend = null, // { value: 12.5, direction: 'up' | 'down' | 'flat' }
  format = 'text' // 'text' | 'currency' | 'percentage' | 'number'
}) => {
  const theme = useTheme()
  const formatValue = (val) => {
    if (loading || val === null || val === undefined) return '-'
    
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: 'CLP',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }).format(val)
      
      case 'percentage':
        return `${parseFloat(val).toFixed(1)}%`
      
      case 'number':
        return new Intl.NumberFormat('es-CL').format(val)
      
      default:
        return val
    }
  }

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up': return <TrendingUp fontSize="small" />
      case 'down': return <TrendingDown fontSize="small" />
      case 'flat': return <TrendingFlat fontSize="small" />
      default: return null
    }
  }

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'up': return 'success'
      case 'down': return 'error'
      case 'flat': return 'default'
      default: return 'default'
    }
  }

  return (
    <Card 
      sx={{ 
        height: '100%',
        position: 'relative',
        overflow: 'visible',
        background: `linear-gradient(135deg, ${theme.palette[color]?.main || theme.palette.primary.main}08 0%, transparent 100%)`,
        border: '1px solid',
        borderColor: `${color}.main`,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderOpacity: 0.1,
        '&:hover': {
          boxShadow: `0 8px 25px ${theme.palette[color]?.main || theme.palette.primary.main}20`,
          transform: 'translateY(-4px)',
          transition: 'all 0.3s ease-in-out',
          borderOpacity: 0.2
        }
      }}
    >
      <CardContent sx={{ p: 1.8 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flexGrow: 1 }}>
            {loading ? (
              <>
                <Skeleton variant="text" width="60%" height={32} />
                <Skeleton variant="text" width="40%" height={24} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
              </>
            ) : (
              <>
                <Typography 
                  variant="h4" 
                  fontWeight={700} 
                  color={`${color}.main`} 
                  sx={{ 
                    fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.7rem' },
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                    mb: 0.3,
                    textShadow: `0 1px 2px ${theme.palette[color]?.main || theme.palette.primary.main}20`
                  }}
                >
                  {formatValue(value)}
                </Typography>
                
                <Typography 
                  variant="body1" 
                  fontWeight={600} 
                  sx={{ 
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    color: 'text.primary',
                    mb: 0.3
                  }}
                >
                  {title}
                </Typography>
                
                {subtitle && (
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      fontSize: { xs: '0.65rem', sm: '0.7rem' },
                      display: 'block'
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
                
                {trend && (
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      icon={getTrendIcon(trend.direction)}
                      label={`${trend.value > 0 ? '+' : ''}${trend.value}%`}
                      color={getTrendColor(trend.direction)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
          
          <Box
            sx={{
              p: 1.5,
              borderRadius: '50%',
              background: `linear-gradient(135deg, ${theme.palette[color]?.main || theme.palette.primary.main} 0%, ${theme.palette[color]?.dark || theme.palette.primary.dark} 100%)`,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 1.5,
              flexShrink: 0,
              width: 40,
              height: 40,
              boxShadow: `0 3px 8px ${theme.palette[color]?.main || theme.palette.primary.main}30`,
              '& > svg': {
                fontSize: '1.4rem'
              }
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default KPICard
